import { useEffect, useState } from "react";
import {
  listUsers, approveUser, rejectUser, deleteUserProfile, setUserRole, setUserPermissions,
} from "@/lib/firestore";
import { USR } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle, XCircle, Trash, UserGear, ShieldWarning } from "@phosphor-icons/react";

const PERM_KEYS = [
  { key: "can_report_damage", label: "Damage" },
  { key: "can_request_tools", label: "Request" },
];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [confirmAdmin, setConfirmAdmin] = useState(null); // user object pending admin confirmation

  const load = async () => {
    const data = await listUsers();
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await approveUser(id); toast.success("Approved"); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const reject = async (id) => {
    try { await rejectUser(id); toast.success("Rejected"); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user? (This removes their profile — their sign-in account must be removed separately in the Firebase console.)")) return;
    try { await deleteUserProfile(id, me.id); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const setRole = async (id, role) => {
    try { await setUserRole(id, role); toast.success("Role updated"); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const handleRoleChange = (u, role) => {
    if (role === "admin") setConfirmAdmin(u);
    else setRole(u.id, role);
  };
  const confirmMakeAdmin = async () => {
    if (!confirmAdmin) return;
    await setRole(confirmAdmin.id, "admin");
    setConfirmAdmin(null);
  };
  const togglePerm = async (u, key) => {
    const next = { ...(u.permissions || {}), [key]: !u.permissions?.[key] };
    try {
      await setUserPermissions(u.id, next);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const list = filter === "all" ? users : users.filter((u) => u.status === filter);
  const counts = users.reduce((a, u) => ({ ...a, [u.status]: (a[u.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// ACCESS CONTROL</div>
          <h1 className="font-display font-black text-4xl">USER MANAGEMENT</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">Approve, control roles and set permissions.</div>
        </div>
        <div className="flex gap-2">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`user-filter-${f}`}
              className={`brutal-btn text-xs px-3 py-2 border transition-colors duration-150 ${
                filter === f ? "bg-[#FF4F00] text-black border-[#FF4F00]" : "border-[#27272A] hover:border-[#FF4F00]"
              }`}
            >
              {f} {counts[f] ? `· ${counts[f]}` : f === "all" ? `· ${users.length}` : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-[#27272A]">
            <tr className="text-left">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">User</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Status</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Role</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Permissions</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Actions</th>
            </tr>
          </thead>
          <tbody className="table-row-alt">
            {list.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#52525B] font-mono">// no users //</td></tr>
            )}
            {list.map((u) => (
              <tr key={u.id} className="border-b border-[#1a1a1e]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 border border-[#27272A] flex items-center justify-center">
                      <UserGear size={14} className="text-[#FF4F00]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{u.name}</div>
                      <div className="font-mono text-xs text-[#52525B]">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                    u.status === "approved" ? "border-[#00E676] text-[#00E676]" :
                    u.status === "pending" ? "border-[#FFD700] text-[#FFD700]" :
                    "border-[#D32F2F] text-[#D32F2F]"
                  }`}>{u.status}</span>
                </td>
                <td className="px-4 py-3">
                  {u.id === me.id ? (
                    <span className="text-xs font-mono">{u.role}</span>
                  ) : (
                    <select
                      data-testid={USR.roleSelect(u.id)}
                      value={u.role}
                      onChange={(e) => handleRoleChange(u, e.target.value)}
                      className="bg-[#0A0A0C] border border-[#27272A] px-2 py-1 text-xs focus:outline-none focus:border-[#FF4F00]"
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {PERM_KEYS.map((p) => {
                      const on = u.permissions?.[p.key];
                      return (
                        <button
                          key={p.key}
                          onClick={() => togglePerm(u, p.key)}
                          disabled={u.id === me.id}
                          data-testid={`perm-${p.key}-${u.id}`}
                          className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors duration-150 ${
                            on ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10" : "border-[#27272A] text-[#52525B]"
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {u.status === "pending" && (
                      <>
                        <button
                          data-testid={USR.approveBtn(u.id)}
                          onClick={() => approve(u.id)}
                          className="brutal-btn text-xs border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-3 py-1.5 flex items-center gap-1 transition-colors duration-150"
                        >
                          <CheckCircle size={12} weight="bold" /> Approve
                        </button>
                        <button
                          data-testid={USR.rejectBtn(u.id)}
                          onClick={() => reject(u.id)}
                          className="brutal-btn text-xs border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-3 py-1.5 flex items-center gap-1 transition-colors duration-150"
                        >
                          <XCircle size={12} weight="bold" /> Reject
                        </button>
                      </>
                    )}
                    {u.status === "approved" && u.id !== me.id && (
                      <button
                        data-testid={USR.rejectBtn(u.id)}
                        onClick={() => reject(u.id)}
                        className="brutal-btn text-xs border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black px-3 py-1.5 transition-colors duration-150"
                      >
                        Revoke
                      </button>
                    )}
                    {u.id !== me.id && (
                      <button
                        data-testid={USR.deleteBtn(u.id)}
                        onClick={() => del(u.id)}
                        className="brutal-btn text-xs border border-[#27272A] text-[#A1A1AA] hover:border-[#D32F2F] hover:text-[#D32F2F] px-3 py-1.5 flex items-center gap-1 transition-colors duration-150"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmAdmin && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmAdmin(null)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-md">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 shrink-0 bg-[#FF4F00] flex items-center justify-center">
                <ShieldWarning weight="bold" size={20} className="text-black" />
              </div>
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-1">// CONFIRM ROLE CHANGE</div>
                <h2 className="font-display font-black text-xl leading-tight">Make {confirmAdmin.name} an admin?</h2>
              </div>
            </div>
            <p className="text-sm text-[#A1A1AA] mb-6">
              Admins get full access to manage tools, categories, locations, and other users — including issuing/receiving tools and approving requests. This can be undone later, but grant it deliberately.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmAdmin(null)}
                className="brutal-btn flex-1 border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] px-4 py-2.5 text-sm transition-colors duration-150"
              >
                Don't change
              </button>
              <button
                onClick={confirmMakeAdmin}
                className="brutal-btn flex-1 bg-[#FF4F00] hover:bg-[#FF6B2B] active:bg-[#E64700] text-black px-4 py-2.5 text-sm font-semibold transition-colors duration-150"
              >
                Make Admin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
